import {useCallback, useEffect, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {Box, Grid, LinearProgress, Typography} from '@mui/material';

import {Loading} from '../../../components/Loading/Loading';
import QuestionNavigator from '../../../components/Navigator/QuestionNavigator';
import {useApiRead} from '../../../hooks/useApiRead';
import {useApiWrite} from '../../../hooks/useApiWrite';
import {useQuestionnaireNavigation} from '../../../hooks/useQuestionnaireNavigation';
import {GetRoutes} from '../../../lib/api/GET/GetRoutes';
import {Post} from '../../../lib/api/POST/Post';
import {PostRoutes} from '../../../lib/api/POST/PostRoutes';
import {CultureTypeResponse} from '../../../types/B2B/B2BTypes';
import {SetOfQuestions} from '../../company/culture/surveys/components/survey-questionnaire/cultural-categories/SetOfQuestions';
import {Item} from '../../company/culture/surveys/types/SurveyQuestionnaireTypes';

const MAX_QUESTION_COUNT = 5;
const INITIAL_QUESTION_COUNT = 3;

export const CompanyLightQuestionnaire = () => {
  const navigate = useNavigate();
  const {t} = useTranslation();
  const {data: questions} = useApiRead<Item[]>({path: GetRoutes.coreItemsForCompanyManagers});
  const [items, setItems] = useState<Item[][]>([]);
  const shouldAddNewQuestionRef = useRef(true);
  const nextQuestionTargetIndexRef = useRef<number | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isCalculatingCultureType, setIsCalculatingCultureType] = useState(false);

  useEffect(() => {
    if (questions?.length) {
      const initialItems = questions.slice(0, INITIAL_QUESTION_COUNT).map((q) => [q]);
      const placeholders: Item[][] = Array.from({length: MAX_QUESTION_COUNT - initialItems.length}, () => []);
      setItems([...initialItems, ...placeholders]);
    }
  }, [questions]);

  const {onSectionIndexChange, sections, questionSections, answerSections, onAnswer, sectionIndex} =
    useQuestionnaireNavigation(items);

  const onSuccess = useCallback(
    (data: CultureTypeResponse) => {
      if (shouldAddNewQuestionRef.current) {
        setIsLoadingMore(false);
        const nextQuestion = data?.nextQuestion as Item | undefined;
        const targetIndex = nextQuestionTargetIndexRef.current;

        if (!nextQuestion || targetIndex === null) {
          return;
        }

        setItems((prev) => {
          if (targetIndex >= prev.length) {
            return prev;
          }

          const nextItems = [...prev];
          nextItems[targetIndex] = [nextQuestion];
          return nextItems;
        });

        nextQuestionTargetIndexRef.current = null;
        onSectionIndexChange(targetIndex);
      } else {
        setIsCalculatingCultureType(false);
        navigate('/b2b/results', {state: data});
      }
    },
    [navigate, onSectionIndexChange]
  );

  const onError = useCallback(() => {
    setIsLoadingMore(false);
    setIsCalculatingCultureType(false);
    nextQuestionTargetIndexRef.current = null;
  }, []);

  const {makeRequest} = useApiWrite<Post.SubmitLightCompanySurveyResults, CultureTypeResponse>({
    method: 'post',
    path: PostRoutes[Post.SubmitLightCompanySurveyResults],
    disableLoadingOverlay: true,
    onSuccess: onSuccess,
    onError: onError,
  });

  const handleNext = () => {
    if (isLoadingMore || !sections[sectionIndex]?.isFinished) {
      return;
    }

    const nextIndex = sectionIndex + 1;
    if (nextIndex < sections.length && !sections[nextIndex]?.isLocked) {
      onSectionIndexChange(sectionIndex + 1);
      return;
    }

    const loadedQuestionCount = items.filter((section) => section.length > 0).length;
    const isOnLastLoadedQuestion = sectionIndex === loadedQuestionCount - 1;

    if (!isOnLastLoadedQuestion || loadedQuestionCount >= MAX_QUESTION_COUNT) {
      return;
    }

    shouldAddNewQuestionRef.current = true;
    nextQuestionTargetIndexRef.current = loadedQuestionCount;
    setIsLoadingMore(true);
    makeRequest({answers: answerSections.flat()});
  };

  const submitAnswers = () => {
    shouldAddNewQuestionRef.current = false;
    setIsCalculatingCultureType(true);

    makeRequest({answers: answerSections.flat()});
  };

  const progress = sections.length ? ((sectionIndex + 1) / sections.length) * 100 : 0;

  return (
    <Box>
      {isCalculatingCultureType && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: {xs: 420, md: 460},
            py: {xs: 2, md: 3},
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 620,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              backgroundColor: '#fff5e8',
              px: {xs: 2.5, md: 4},
              py: {xs: 4, md: 5},
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
            }}
          >
            <Typography
              variant='h4'
              fontWeight={700}
              textAlign='center'
              sx={{fontSize: {xs: '1.75rem', md: '2.125rem'}, lineHeight: 1.25}}
            >
              {t('b2b.questionnaire.calculatingCultureType')}
            </Typography>
            <Loading />
          </Box>
        </Box>
      )}
      {!isCalculatingCultureType && (
        <>
          <Typography variant='h5' fontWeight={700} mb={1}>
            {t('b2b.questionnaire.title')}
          </Typography>
          <Box display='flex' alignItems='center' gap={2} mb={1}>
            <LinearProgress variant='determinate' value={progress} sx={{flexGrow: 1, height: 8, borderRadius: 4}} />
            <Typography variant='body2' color='text.secondary' whiteSpace='nowrap'>
              {sectionIndex + 1} / {sections.length}
            </Typography>
          </Box>
          <Grid container columns={12}>
            <Grid item xs={12}>
              <SetOfQuestions
                questions={questionSections[sectionIndex] || []}
                answers={answerSections[sectionIndex] || []}
                onAnswer={onAnswer}
                usageCase='b2bLight'
              />
            </Grid>
            <Grid item xs={12}>
              <QuestionNavigator
                sections={sections}
                currentPosition={sectionIndex}
                onIndexChange={onSectionIndexChange}
                onNext={handleNext}
                onComplete={submitAnswers}
                canComplete={true}
                isLoadingMore={isLoadingMore}
              />
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};
